import isObject from 'lodash-es/isObject';

import { create, v, w } from '@dojo/framework/core/vdom';
import { theme } from '@dojo/widgets/middleware/theme';

// make generated .d.ts file valid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { WNodeFactory, WidgetProperties, DNode, SupportedClassName } from '@dojo/framework/core/interfaces';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ThemeProperties } from '@dojo/framework/core/middleware/theme';

import { TableAttrs } from 'book-widgets/interfaces';
import { TopicDetails } from 'lcc-state/interfaces';

import { TableHead } from './table-head';
import { TableBody } from './table-body';
import { knownClasses } from './table-util';

import css from '@holmescorp/book-widgets/css/topicpage.m.css';

export interface ColSpec {
	width: number;
	columnNumber: number;
	name: string;
	separator: SupportedClassName;
}

export type ColSpecMap = Map<string, ColSpec>;

export interface TableTgroupProperties {
	tgroup: TopicDetails;
	tableattrs: TableAttrs;
}

const factory = create({ theme }).properties<TableTgroupProperties>();

/*
 * column widths from DITA are in inches. There are 6.0 rem / inch.
 * column specs are in colspec attributes
 */
const calc_col_widths = ({
	colspecs,
	cols,
	rowsep,
	themeCss
}: {
	colspecs: { [key: string]: TopicDetails }[];
	cols: number;
	rowsep: number;
	themeCss: any;
}): ColSpecMap => {
	const specMap = new Map<string, ColSpec>();
	specMap.set('tgroup', {
		columnNumber: cols,
		width: 0,
		name: 'tgroup',
		separator: rowsep === 1 ? themeCss.rowSeparator : themeCss.noRowSeparator
	});

	colspecs.forEach((item, idx) => {
		const {
			colwidth = '0.5in',
			colname = idx + 1,
			colsep = 1,
			colnum = idx + 1
		} = item.colspec.attributes as TableAttrs;
		const inches: string = colwidth.replace(/[^0-9.]+/g, '');
		const name = `col-${colname}`;
		const calcedSpec: ColSpec = {
			columnNumber: colnum,
			width: Number(inches) * 6.0,
			name,
			separator: colsep === 1 ? themeCss.columnSeparator : themeCss.noColumnSeparator
		};
		specMap.set(name, calcedSpec);
	});

	return specMap;
};

/**
 * Do the heavy lifting of converting from a DITA table structure to HTML.
 */
export const TableTgroup = factory(({ properties, middleware: { theme } }) => {
	const { tgroup, tableattrs } = properties();
	const themeCss = theme.classes(css);
	const { children = [], attributes = {} as TableAttrs } = tgroup;
	const colspecs = children.filter((kid) => {
		return !!kid.colspec;
	});
	const numberOfColumns = (attributes as TableAttrs).cols || colspecs.length;
	const widths = calc_col_widths({
		colspecs,
		cols: numberOfColumns,
		rowsep: (attributes as TableAttrs).rowsep || 1,
		themeCss
	});
	const theadobj = children.find((kid) => {
		return isObject(kid) && 'thead' in kid;
	});
	const thead = theadobj ? theadobj.thead : null;
	const tbody = children.find((kid) => {
		return isObject(kid) && 'tbody' in kid;
	})!.tbody;

	const { outputclass: tblclass, colsep = 1, rowsep = 1 } = tableattrs;
	const classes = [];
	if (tblclass) {
		const fixedclass = tblclass.replace('.', '_').replace('-', '_');
		if (knownClasses.has(fixedclass)) {
			classes.push((themeCss as any)[fixedclass]);
		}
	}
	classes.push(rowsep === 1 ? themeCss.rowSeparator : themeCss.noRowSeparator);
	classes.push(colsep === 1 ? themeCss.columnSeparator : themeCss.noColumnSeparator);

	return v('table', { classes }, [thead && w(TableHead, { thead, widths }), w(TableBody, { tbody, widths })]);
});
